import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../../api/client";
import PaidAdsCampaignTable from "./PaidAdsCampaignTable";
import PaidAdsCampaignForm from "./PaidAdsCampaignForm";
import PaidAdsProviderSetup from "./PaidAdsProviderSetup";

function useMessageListener(onMetaToken) {
  useEffect(() => {
    const fn = (ev) => {
      const d = ev.data;
      if (d && d.provider === "meta-ads" && d.accessToken) {
        onMetaToken(d.accessToken);
      }
    };
    window.addEventListener("message", fn);
    return () => window.removeEventListener("message", fn);
  }, [onMetaToken]);
}

const PaidAds = () => {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [error, setError] = useState(null);
  const [connections, setConnections] = useState(null);
  const [campaignRows, setCampaignRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [listPage, setListPage] = useState(0);
  const [listPageSize, setListPageSize] = useState(50);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [configureOpen, setConfigureOpen] = useState(false);
  const [configureProvider, setConfigureProvider] = useState("meta");
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [metaAccountPick, setMetaAccountPick] = useState("");
  const [googleCustomers, setGoogleCustomers] = useState([]);
  const [googleCustomerPick, setGoogleCustomerPick] = useState("");
  const [metaManualToken, setMetaManualToken] = useState("");

  const loadConnections = useCallback(async () => {
    try {
      const res = await fetchData("/ads/connections", "GET");
      setConnections(res?.data ?? null);
    } catch (e) {
      setError(e?.message || t("paidAdsPage.loadError"));
    }
  }, [t]);

  const loadCampaigns = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await fetchData("/ads/workspace-campaigns?limit=200&offset=0", "GET");
      const list = res?.data?.campaigns ?? res?.campaigns ?? [];
      setCampaignRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || t("paidAdsPage.campaignListLoadError"));
    } finally {
      setListLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadConnections();
    loadCampaigns();
  }, [loadConnections, loadCampaigns]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("google") === "connected") {
      loadConnections();
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadConnections]);

  const onMetaToken = useCallback(
    async (accessToken) => {
      try {
        await fetchData("/ads/meta/token", "POST", { accessToken });
        await loadConnections();
      } catch (e) {
        setError(e?.message || t("paidAdsPage.metaTokenError"));
      }
    },
    [loadConnections, t]
  );

  useMessageListener(onMetaToken);

  const submitMetaManualToken = useCallback(async () => {
    const accessToken = String(metaManualToken || "").trim();
    if (!accessToken) return;
    setError(null);
    try {
      await fetchData("/ads/meta/token", "POST", { accessToken });
      setMetaManualToken("");
      await loadConnections();
    } catch (e) {
      setError(e?.message || t("paidAdsPage.metaTokenError"));
    }
  }, [metaManualToken, loadConnections, t]);

  const openConfigure = (provider) => {
    setConfigureProvider(provider);
    setConfigureOpen(true);
    setError(null);
  };

  const openMetaOAuth = async () => {
    setError(null);
    try {
      const res = await fetchData("/ads/meta/oauth-url", "GET");
      const url = res?.data?.url || res?.url;
      if (!url) throw new Error("URL");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e?.message || t("paidAdsPage.oauthError"));
    }
  };

  const openGoogleOAuth = async () => {
    setError(null);
    try {
      const res = await fetchData("/ads/google/oauth-url", "GET");
      const url = res?.data?.url || res?.url;
      if (!url) throw new Error("URL");
      window.location.href = url;
    } catch (e) {
      setError(e?.message || t("paidAdsPage.oauthError"));
    }
  };

  const loadMetaAccounts = async () => {
    setError(null);
    try {
      const res = await fetchData("/ads/meta/ad-accounts", "GET");
      const list = res?.data?.accounts || res?.accounts || [];
      setMetaAccounts(list);
    } catch (e) {
      setError(e?.message || t("paidAdsPage.metaAccountsError"));
    }
  };

  const saveMetaAccount = async () => {
    const row = metaAccounts.find((a) => String(a.id) === String(metaAccountPick));
    if (!row) return;
    setError(null);
    try {
      await fetchData("/ads/meta/select-account", "POST", {
        adAccountId: row.account_id || row.id,
        adAccountName: row.name || null,
      });
      await loadConnections();
    } catch (e) {
      setError(e?.message || t("paidAdsPage.saveAccountError"));
    }
  };

  const loadGoogleCustomers = async () => {
    setError(null);
    try {
      const res = await fetchData("/ads/google/customers", "GET");
      const list = res?.data?.customers || res?.customers || [];
      setGoogleCustomers(list);
    } catch (e) {
      setError(e?.message || t("paidAdsPage.googleCustomersError"));
    }
  };

  const saveGoogleCustomer = async () => {
    const row = googleCustomers.find((c) => String(c.customerId) === String(googleCustomerPick));
    if (!row?.customerId) return;
    setError(null);
    try {
      await fetchData("/ads/google/select-customer", "POST", {
        customerId: row.customerId,
        customerName: row.resourceName || null,
      });
      await loadConnections();
    } catch (e) {
      setError(e?.message || t("paidAdsPage.saveCustomerError"));
    }
  };

  const disconnectMeta = async () => {
    try {
      await fetchData("/ads/meta/connection", "DELETE");
      await loadConnections();
      setMetaAccounts([]);
      setConfigureOpen(false);
    } catch (e) {
      setError(e?.message || t("paidAdsPage.disconnectError"));
    }
  };

  const disconnectGoogle = async () => {
    try {
      await fetchData("/ads/google/connection", "DELETE");
      await loadConnections();
      setGoogleCustomers([]);
      setConfigureOpen(false);
    } catch (e) {
      setError(e?.message || t("paidAdsPage.disconnectError"));
    }
  };

  const providerSetupProps = {
    connections,
    metaAccounts,
    metaAccountPick,
    onMetaAccountPickChange: setMetaAccountPick,
    googleCustomers,
    googleCustomerPick,
    onGoogleCustomerPickChange: setGoogleCustomerPick,
    onOpenMetaOAuth: openMetaOAuth,
    onOpenGoogleOAuth: openGoogleOAuth,
    onLoadMetaAccounts: loadMetaAccounts,
    onLoadGoogleCustomers: loadGoogleCustomers,
    onSaveMetaAccount: saveMetaAccount,
    onSaveGoogleCustomer: saveGoogleCustomer,
    onDisconnectMeta: disconnectMeta,
    onDisconnectGoogle: disconnectGoogle,
    metaManualToken,
    onMetaManualTokenChange: setMetaManualToken,
    onSubmitMetaManualToken: submitMetaManualToken,
  };

  const filteredRows = campaignRows.filter((r) => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return true;
    return (
      String(r.name || "").toLowerCase().includes(q) ||
      String(r.provider || "").toLowerCase().includes(q) ||
      String(r.external_campaign_id || "").toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    setListPage(0);
  }, [search]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(filteredRows.length / listPageSize) || 1);
    if (listPage >= pages) setListPage(Math.max(0, pages - 1));
  }, [filteredRows.length, listPageSize, listPage]);

  const refetchCampaignById = async (id) => {
    if (!id) return;
    try {
      const res = await fetchData(`/ads/workspace-campaigns/${id}`, "GET");
      const c = res?.data?.campaign ?? res?.campaign;
      if (c) setSelectedCampaign(c);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: selectedCampaign ? 1 : 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: isMobile ? 0 : undefined }}>
          {t("paidAdsPage.title")}
        </Typography>
        {!selectedCampaign && (
          <Button variant="contained" color="primary" fullWidth={isMobile} onClick={() => setSelectedCampaign({})}>
            {t("paidAdsPage.addCampaign")}
          </Button>
        )}
      </Box>

      {!selectedCampaign && (
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2, maxWidth: 720 }}>
          {t("paidAdsPage.listPageSubtitle")}
        </Typography>
      )}

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" sx={{ "& .MuiAlert-message": { width: "100%" } }} onClose={() => setError(null)}>
            <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-line" }}>
              {error}
            </Typography>
          </Alert>
        </Box>
      )}

      {selectedCampaign ? (
        <PaidAdsCampaignForm
          key={selectedCampaign?.id != null ? String(selectedCampaign.id) : "new-campaign"}
          campaign={selectedCampaign}
          connections={connections}
          onClose={() => {
            setSelectedCampaign(null);
            loadCampaigns();
          }}
          onAfterSave={loadCampaigns}
          onCampaignCreated={(row) => row && setSelectedCampaign(row)}
          onAfterPublish={async (id) => {
            await loadCampaigns();
            await refetchCampaignById(id);
          }}
          onConfigureProvider={(p) => openConfigure(p)}
          providerSetupProps={providerSetupProps}
        />
      ) : (
        <PaidAdsCampaignTable
          rows={filteredRows}
          isLoading={listLoading}
          search={search}
          onSearchChange={setSearch}
          onEdit={(row) => setSelectedCampaign(row)}
          connections={connections}
          page={listPage}
          pageSize={listPageSize}
          onPageChange={setListPage}
          onPageSizeChange={(size) => {
            setListPageSize(size);
            setListPage(0);
          }}
          onRefresh={() => {
            loadConnections();
            loadCampaigns();
          }}
        />
      )}

      <Dialog open={configureOpen} onClose={() => setConfigureOpen(false)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>
          {configureProvider === "meta" ? t("paidAdsPage.metaTitle") : t("paidAdsPage.googleTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("paidAdsPage.dialogAssistantHint")}
          </Typography>
          <PaidAdsProviderSetup provider={configureProvider} {...providerSetupProps} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigureOpen(false)}>{t("paidAdsPage.close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaidAds;
